"use client";

import * as React from "react";
import {
  ChevronRight,
  Phone,
  ShieldCheck,
  FileText,
  MessageCircle,
  Inbox,
  Globe,
  CalendarCheck,
  Wallet,
  Sparkles,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { MarketingFeature } from "@/lib/api";

/**
 * No per-feature icon field exists in the admin form (only title/short/full
 * description/image), so this maps common keywords to a sensible icon for
 * the fallback visual shown until a real image is uploaded for that feature.
 * Matches the title only (not the longer description, which is prone to
 * incidental substring collisions -- e.g. "recall campaigns" containing
 * "call") and checks the most specific/unambiguous keywords first.
 */
function iconForFeature(feature: MarketingFeature) {
  const text = feature.title.toLowerCase();
  if (text.includes("whatsapp") && text.includes("inbox")) return Inbox;
  if (text.includes("whatsapp")) return MessageCircle;
  if (text.includes("prescription") || text.includes("safety")) return ShieldCheck;
  if (text.includes("scribe") || text.includes("note")) return FileText;
  if (text.includes("website") || text.includes("white-label") || text.includes("white label")) return Globe;
  if (text.includes("appointment") || text.includes("emr") || text.includes("schedul")) return CalendarCheck;
  if (text.includes("billing") || text.includes("wallet") || text.includes("finance")) return Wallet;
  if (/\b(voice|call|phone)\b/.test(text)) return Phone;
  return Sparkles;
}

function FeatureFallbackVisual({ feature }: { feature: MarketingFeature }) {
  return (
    <div className="relative flex h-56 w-full items-center justify-center overflow-hidden bg-gradient-to-br from-primary/15 via-blue-500/10 to-primary/5 sm:h-72">
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {React.createElement(iconForFeature(feature), { className: "relative size-20 text-primary/70", strokeWidth: 1.25 })}
    </div>
  );
}

function FeaturesExplorer({ features }: { features: MarketingFeature[] }) {
  const [activeId, setActiveId] = React.useState(features[0]?.id);
  const active = features.find((f) => f.id === activeId) ?? features[0];

  if (!active) return null;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,320px)_1fr]">
      <ul className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
        {features.map((feature) => {
          const isActive = feature.id === active.id;
          return (
            <li key={feature.id} className="shrink-0 lg:shrink">
              <button
                type="button"
                onClick={() => setActiveId(feature.id)}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-4 py-3 text-left text-sm transition-colors",
                  isActive
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                <span>
                  <span className="block font-medium">{feature.title}</span>
                  <span className="mt-0.5 hidden text-xs text-muted-foreground lg:block">{feature.short_description}</span>
                </span>
                <ChevronRight className={cn("size-4 shrink-0 transition-transform", isActive && "lg:translate-x-0.5")} />
              </button>
            </li>
          );
        })}
      </ul>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        {active.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element -- platform-uploaded, on the Laravel public disk.
          <img src={active.image_url} alt={active.title} className="h-56 w-full object-cover sm:h-72" />
        ) : (
          <FeatureFallbackVisual feature={active} />
        )}
        <div className="p-6 sm:p-8">
          <h3 className="text-xl font-semibold text-foreground">{active.title}</h3>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{active.full_description}</p>
        </div>
      </div>
    </div>
  );
}

export { FeaturesExplorer };
